import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getApiUrl } from '../config/api';
import { createPortal } from 'react-dom';
import { InventorySearchService } from '../services/inventorySearchService';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBranch } from '../contexts/BranchContext';
import { checkPermission } from '../utils/permissions';
import AccessDenied from './AccessDenied';
import LoadingSpinner from './LoadingSpinner';
import Toast from './Toast';
import { BarcodeScanner } from './BarcodeScanner';
import { dealerService, Dealer as RealDealer } from '../lib/dealerService';
import { formatCurrency, getCurrencySymbol } from '../utils/currency';

interface Dealer {
  id: string;
  dealerName: string;
  shopName: string;
  shopAddress: string;
  phoneNumber: string;
  email?: string;
  gstNumber?: string;
  creditTerms: number; // days
  notes?: string;
  totalBusiness: number;
  outstandingBalance: number;
  paymentStatus: 'Good' | 'Overdue' | 'New';
  registrationDate: string;
  lastOrderDate: string;
  averageOrderValue: number;
  totalOrders: number;
}

interface DealerOrder {
  id: string;
  orderId: string;
  dealerId: string;
  dealerName: string;
  orderDate: string;
  totalAmount: number;
  advancePaid: number;
  balancePending: number;
  paymentStatus: 'Pending' | 'Partial' | 'Paid';
  itemsCount: number;
  deliveryDate?: string;
  deliveryStatus: 'Pending' | 'Delivered' | 'Cancelled';
  notes?: string;
}

interface InventoryItem {
  id: number;
  inventory_id: string;
  imei_mac_address: string;
  phone_model: string;
  category: string;
  buying_price: number;
  selling_price: number;
  service_expense: number;
  package_expense: number;
  status: string;
  branch_id: number;
  branch_name: string;
  supplier?: {
    id: number;
    name: string;
    phone_number: string;
  };
}

const DealerManagement: React.FC = () => {
  const { user, token } = useAuth();
  const { selectedBranchId, selectedBranchName, isAllBranches } = useBranch();
  const navigate = useNavigate();

  // State management
  const [isNewSaleModalOpen, setIsNewSaleModalOpen] = useState(false);
  const [isViewOrderModalOpen, setIsViewOrderModalOpen] = useState(false);
  const [isEditOrderModalOpen, setIsEditOrderModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [selectedDealer, setSelectedDealer] = useState<RealDealer | null>(null);
  const [editOrderData, setEditOrderData] = useState<any>(null);
  
  // Modal dropdown states
  const [isModalCreditTermsDropdownOpen, setIsModalCreditTermsDropdownOpen] = useState(false);
  const [isModalBranchDropdownOpen, setIsModalBranchDropdownOpen] = useState(false);
  
  // Modal form states
  const [selectedModalCreditTerms, setSelectedModalCreditTerms] = useState('30 days');
  const [inventorySearchTerm, setInventorySearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<InventoryItem[]>([]);
  const [availableInventory, setAvailableInventory] = useState<InventoryItem[]>([]);
  const [isLoadingInventory, setIsLoadingInventory] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [cartItems, setCartItems] = useState<any[]>([]);
  
  // Barcode scanner states - automatically active when modal is open
  const [lastScannedValue, setLastScannedValue] = useState<string>('');
  const [advanceAmount, setAdvanceAmount] = useState('');
  const [dealerPrice, setDealerPrice] = useState('');
  const [notes, setNotes] = useState('');
  
  // Dealer selection states
  const [allDealers, setAllDealers] = useState<RealDealer[]>([]);
  const [isLoadingDealers, setIsLoadingDealers] = useState(false);
  const [isDealerDropdownOpen, setIsDealerDropdownOpen] = useState(false);
  
  // Branch selection states for form
  const [availableBranches, setAvailableBranches] = useState<any[]>([]);
  const [selectedFormBranchId, setSelectedFormBranchId] = useState<string>('');
  const [selectedFormBranchName, setSelectedFormBranchName] = useState<string>('');
  
  // Dealer orders states
  const [dealerOrders, setDealerOrders] = useState<any[]>([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(true);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearchTerm, setActiveSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState('All Orders');
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  
  // Date Range Filter States
  const [selectedTimeFilter, setSelectedTimeFilter] = useState('All Time');
  const [isTimeFilterOpen, setIsTimeFilterOpen] = useState(false);
  const [isCustomDateRange, setIsCustomDateRange] = useState(false);
  const [isCustomDateModalOpen, setIsCustomDateModalOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [tempStartDate, setTempStartDate] = useState('');
  const [tempEndDate, setTempEndDate] = useState('');
  
  // Refs for dropdown click-outside handling
  const filterDropdownRef = useRef<HTMLDivElement>(null);
  const timeFilterRef = useRef<HTMLDivElement>(null);
  
  // Toast state
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
    isVisible: boolean;
  }>({
    message: '',
    type: 'info',
    isVisible: false
  });
  
  // Modal dropdown options
  const modalCreditTermsOptions = ['15 days', '30 days', '45 days', '60 days', '90 days'];
  
  // Toast helper functions
  const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning') => {
    setToast({
      message,
      type,
      isVisible: true
    });
  };

  const hideToast = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  // Time filter options for dealer orders (focused on sale_date)
  const timeFilterOptions = [
    'All Time',
    'This Month',
    'Last Month', 
    'This Year',
    'Last Year',
    'Custom'
  ];

  // Handle time filter selection
  const handleTimeFilterSelect = (option: string) => {
    setIsTimeFilterOpen(false);
    if (option === 'Custom') {
      // Set default custom range if not already set
      if (!tempStartDate || !tempEndDate) {
        const today = new Date();
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(today.getDate() - 30);
        
        setTempEndDate(today.toISOString().split('T')[0]);
        setTempStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
      }
      setIsCustomDateModalOpen(true);
    } else {
      setSelectedTimeFilter(option);
      setIsCustomDateRange(false);
    }
  };

  // Handle custom date range modal
  const handleCustomDateApply = () => {
    if (tempStartDate && tempEndDate) {
      setCustomStartDate(tempStartDate);
      setCustomEndDate(tempEndDate);
      setSelectedTimeFilter('Custom');
      setIsCustomDateRange(true);
      setIsCustomDateModalOpen(false);
    }
  };

  const handleCustomDateCancel = () => {
    setIsCustomDateModalOpen(false);
    // Reset temp dates to current custom dates
    setTempStartDate(customStartDate);
    setTempEndDate(customEndDate);
  };

  // Fetch available branches function
  const fetchAvailableBranches = async () => {
    if (!token) return;
    
    try {
      const response = await fetch(getApiUrl("branches"), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.branches) {
          setAvailableBranches(result.branches);
          
          // Auto-select branch for branch managers
          if (user?.role === 'branch_manager' && result.branches.length > 0) {
            const userBranch = result.branches[0];
            setSelectedFormBranchId(userBranch.id);
            setSelectedFormBranchName(userBranch.branch_name);
          }
        } else {
          console.error('Failed to fetch branches:', result.error);
          // Use fallback sample branches
          const sampleBranches = [
            { id: '1', branch_name: 'Main Branch - Mumbai' },
            { id: '2', branch_name: 'Branch 2 - Delhi' },
            { id: '3', branch_name: 'Branch 3 - Bangalore' }
          ];
          setAvailableBranches(sampleBranches);
          
          // Auto-select first branch
          if (user?.role === 'branch_manager') {
            setSelectedFormBranchId(sampleBranches[0].id);
            setSelectedFormBranchName(sampleBranches[0].branch_name);
          }
        }
      } else {
        console.error('Failed to fetch branches:', response.status);
        // Use fallback sample branches
        const sampleBranches = [
          { id: '1', branch_name: 'Main Branch - Mumbai' },
          { id: '2', branch_name: 'Branch 2 - Delhi' }
        ];
        setAvailableBranches(sampleBranches);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
      // Use fallback sample branches
      const sampleBranches = [
        { id: '1', branch_name: 'Main Branch - Mumbai' },
        { id: '2', branch_name: 'Branch 2 - Delhi' }
      ];
      setAvailableBranches(sampleBranches);
    }
  };

  // Fetch all dealers function with enhanced statistics
  const fetchAllDealers = async () => {
    setIsLoadingDealers(true);
    try {
      console.log('Fetching dealers from API with enhanced statistics...');
      // Use the new API endpoint with statistics for better performance
      const { data, error } = await dealerService.getAllDealers(true);
      
      if (error) {
        console.error('Error fetching dealers:', error);
        // Use fallback sample data when API fails
        const sampleDealers: RealDealer[] = [
          {
            id: '1',
            name: 'Rajesh Electronics',
            shop_name: 'Mobile World',
            shop_address: 'Shop No. 15, Electronics Market, Mumbai, MH 400001',
            phone: '+91 98765 43210',
            created_at: '2023-03-15',
            updated_at: '2024-01-15',
            totalBusiness: 850000,
            outstandingBalance: 125000,
            totalOrders: 19,
            lastOrderDate: '2024-01-15',
            averageOrderValue: 45000,
            paymentStatus: 'Good'
          },
          {
            id: '2',
            name: 'Amit Kumar',
            shop_name: 'TechZone Solutions',
            shop_address: 'A-123, Connaught Place, Delhi, DL 110001',
            phone: '+91 87654 32109',
            created_at: '2023-06-20',
            updated_at: '2024-01-10',
            totalBusiness: 650000,
            outstandingBalance: 85000,
            totalOrders: 17,
            lastOrderDate: '2024-01-10',
            averageOrderValue: 38500,
            paymentStatus: 'Overdue'
          },
          {
            id: '3',
            name: 'Priya Sharma',
            shop_name: 'Digital Hub',
            shop_address: 'Plot 45, IT Park, Bangalore, KA 560001',
            phone: '+91 76543 21098',
            created_at: '2023-09-10',
            updated_at: '2024-01-08',
            totalBusiness: 420000,
            outstandingBalance: 65000,
            totalOrders: 12,
            lastOrderDate: '2024-01-08',
            averageOrderValue: 35000,
            paymentStatus: 'Good'
          }
        ];
        setAllDealers(sampleDealers);
        showToast('Using sample dealer data (API connection failed)', 'info');
      } else if (data && data.length > 0) {
        console.log('Dealers loaded successfully from API:', data.length);
        setAllDealers(data);
      } else {
        console.log('No dealers found in database');
        setAllDealers([]);
        showToast('No dealers found. Click "Add New" to create your first dealer.', 'info');
      }
    } catch (error) {
      console.error('Error loading dealers:', error);
      // Use fallback sample data when network/other errors occur
      const sampleDealers: RealDealer[] = [
        {
          id: '1',
          name: 'Rajesh Electronics',
          shop_name: 'Mobile World',
          shop_address: 'Shop No. 15, Electronics Market, Mumbai, MH 400001',
          phone: '+91 98765 43210',
          created_at: '2023-03-15',
          updated_at: '2024-01-15'
        },
        {
          id: '2',
          name: 'Amit Kumar',
          shop_name: 'TechZone Solutions',
          shop_address: 'A-123, Connaught Place, Delhi, DL 110001',
          phone: '+91 87654 32109',
          created_at: '2023-06-20',
          updated_at: '2024-01-10'
        }
      ];
      setAllDealers(sampleDealers);
      showToast('Using sample dealer data (Network error)', 'warning');
    } finally {
      setIsLoadingDealers(false);
    }
  };


  // Handle dealer selection from dropdown
  const handleDealerSelect = (dealer: RealDealer) => {
    setSelectedDealer(dealer);
    setIsDealerDropdownOpen(false);
  };

  // Handle branch selection from dropdown
  const handleFormBranchSelect = (branchId: string, branchName: string) => {
    setSelectedFormBranchId(branchId);
    setSelectedFormBranchName(branchName);
    setIsModalBranchDropdownOpen(false);
  };

  // Handle navigation to dealer management in settings
  const handleAddNewDealer = () => {
    navigate('/settings?tab=dealers');
  };

  // Fetch dealer orders from database
  // Search function - triggers search with current searchTerm
  const handleSearch = () => {
    setActiveSearchTerm(searchTerm);
    // The filtering will happen in getFilteredAndSortedOrders using activeSearchTerm
  };

  // Clear search function
  const handleClearSearch = () => {
    setSearchTerm('');
    setActiveSearchTerm('');
  };

  // CSV Export functionality
  const exportToCSV = () => {
    const filteredData = getFilteredAndSortedOrders();
    
    if (filteredData.length === 0) {
      showToast('No data to export', 'warning');
      return;
    }

    // Define CSV headers
    const headers = [
      'Invoice ID',
      'Dealer Name',
      'Shop Name', 
      'Order Date',
      'Items',
      'Total Amount',
      'Total Profit',
      'Advance Paid',
      'Balance Pending',
      'Payment Status',
      'Notes'
    ];

    // Convert data to CSV format
    const csvData = filteredData.map(order => {
      const itemsDescription = order.items_count === 1 
        ? order.item_details?.phone_model || 'Single Item'
        : `${order.items_count} Items`;
      
      return [
        order.invoice_id || '',
        order.dealer_name || '',
        order.dealer_shop || '',
        new Date(order.sale_date).toLocaleDateString('en-IN'),
        itemsDescription,
        formatCurrency(order.total_sale_price),
        formatCurrency(order.total_profit),
        formatCurrency(order.advance_amount),
        formatCurrency(order.pending_amount),
        order.status === 'pending' ? 'Pending' : 
        order.status === 'partial_paid' ? 'Partial Payment' : 'Fully Paid',
        order.notes || ''
      ];
    });

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => 
        row.map(cell => 
          // Escape commas and quotes in cell content
          typeof cell === 'string' && (cell.includes(',') || cell.includes('"')) 
            ? `"${cell.replace(/"/g, '""')}"` 
            : cell
        ).join(',')
      )
    ].join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    
    // Generate filename with current date and filter info
    const today = new Date().toISOString().split('T')[0];
    const filterInfo = filterBy !== 'All Orders' ? `_${filterBy.replace(/\s+/g, '_')}` : '';
    const searchInfo = activeSearchTerm ? `_Search_${activeSearchTerm.replace(/\s+/g, '_')}` : '';
    link.setAttribute('download', `Dealer_Orders_${today}${filterInfo}${searchInfo}.csv`);
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    showToast(`Exported ${filteredData.length} dealer orders to CSV`, 'success');
  };

  const fetchDealerOrders = useCallback(async () => {
    if (!token) return;
    
    setIsLoadingOrders(true);
    try {
      // Build query parameters for branch filtering
      const params = new URLSearchParams();
      
      // Apply branch filtering logic
      if (user?.role === 'branch_manager') {
        // Branch managers see only their assigned branch orders
        if (selectedBranchId) {
          params.append('branch_id', selectedBranchId);
        }
      } else {
        // For admin and inventory managers, apply branch filter if not "All Branches"
        if (!isAllBranches && selectedBranchId) {
          params.append('branch_id', selectedBranchId);
        }
      }

      // Add date filtering
      if (selectedTimeFilter !== 'All Time') {
        if (isCustomDateRange && customStartDate && customEndDate) {
          params.append('date_from', customStartDate);
          params.append('date_to', customEndDate);
          params.append('date_range', 'custom');
        } else {
          params.append('date_range', selectedTimeFilter.toLowerCase().replace(' ', '_'));
        }
      }

      const url = params.toString() 
        ? getApiUrl(`dealers/orders?${params.toString()}`)
        : getApiUrl("dealers/orders");

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setDealerOrders(result.orders || []);
        } else {
          console.error('Failed to fetch dealer orders:', result.error);
          setDealerOrders([]);
        }
      } else {
        console.error('Failed to fetch dealer orders:', response.status);
        setDealerOrders([]);
      }
    } catch (error) {
      console.error('Error fetching dealer orders:', error);
      setDealerOrders([]);
    } finally {
      setIsLoadingOrders(false);
    }
  }, [token, selectedBranchId, isAllBranches, user?.role, selectedTimeFilter, isCustomDateRange, customStartDate, customEndDate]);
  
  // Fetch available inventory from API
  const fetchAvailableInventory = async () => {
    if (!token) return;
    
    try {
      setIsLoadingInventory(true);
      const params = new URLSearchParams();
      params.append('status', 'ready'); // Only fetch ready items
      params.append('limit', '100'); // Get more items for search
      
      // Apply branch filtering for branch managers
      if (user?.role === 'branch_manager') {
        if (selectedBranchId) {
          params.append('branch_id', selectedBranchId);
        }
      } else {
        // For admin and inventory managers, apply branch filter if not "All Branches"
        if (!isAllBranches && selectedBranchId) {
          params.append('branch_id', selectedBranchId);
        }
      }
      
      const response = await fetch(getApiUrl(`inventory?${params.toString()}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const transformedData = data.inventory.map((item: any) => ({
            id: item.id,
            inventory_id: item.inventory_id,
            imei_mac_address: item.imei_mac_address,
            phone_model: item.phone_model,
            category: item.category,
            buying_price: item.buying_price,
            selling_price: item.selling_price,
            service_expense: item.service_expense || 0,
            package_expense: item.package_expense || 0,
            status: item.status,
            branch_id: item.branch_id,
            branch_name: item.branch_name,
            supplier: item.supplier
          }));
          setAvailableInventory(transformedData);
        }
      }
    } catch (error) {
      console.error('Error fetching inventory:', error);
    } finally {
      setIsLoadingInventory(false);
    }
  };
  
  // Helper function for inventory search that can be used by both manual and barcode
  const performInventorySearch = async (searchTerm: string) => {
    const trimmedSearchTerm = searchTerm.trim();
    if (!token || !trimmedSearchTerm) {
      setSearchResults([]);
      setHasSearched(false);
      return;
    }

    try {
      setIsLoadingInventory(true);
      setHasSearched(true);
      
      const result = await InventorySearchService.getAvailableInventory(token, trimmedSearchTerm, {
        branchId: selectedBranchId || undefined,
        excludeCartItems: cartItems,
        userRole: user?.role,
        isAllBranches
      });

      if (result.success) {
        // Transform data to match local InventoryItem interface
        const transformedData = result.items.map((item: any) => ({
          ...item,
          branch_name: item.branches?.branch_name || 'Unknown Branch'
        }));
        setSearchResults(transformedData);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Error searching inventory:', error);
      setSearchResults([]);
    } finally {
      setIsLoadingInventory(false);
    }
  };

  // Manual search function
  const handleInventorySearch = async () => {
    await performInventorySearch(inventorySearchTerm);
  };

  // Barcode scan handler - just sets the search term and triggers the same search logic
  const handleBarcodeScan = async (scannedValue: string) => {
    console.log('🔍 Barcode scanned in DealerManagement:', scannedValue);
    
    // Prevent duplicate scans
    if (scannedValue === lastScannedValue) {
      return;
    }
    
    // Set the scanned value and track it
    setInventorySearchTerm(scannedValue);
    setLastScannedValue(scannedValue);
    
    // Use the same search logic as manual search
    await performInventorySearch(scannedValue);
  };

  // Cart management functions
  const addToCart = (item: InventoryItem, price: number) => {
    const cartItem = {
      inventory_item: item,
      selling_price: price
    };
    setCartItems(prev => [...prev, cartItem]);
    setSearchResults([]);
    setInventorySearchTerm('');
    setHasSearched(false);
  };

  const removeFromCart = (cartItemId: number) => {
    setCartItems(prev => prev.filter(item => item.inventory_item.id !== cartItemId));
    // Reset search state so user can search for the removed item again
    if (hasSearched) {
      setSearchResults([]);
      setInventorySearchTerm('');
      setHasSearched(false);
    }
  };

  const updateCartItemPrice = (cartItemId: number, newPrice: number) => {
    setCartItems(prev => 
      prev.map(item => 
        item.inventory_item.id === cartItemId 
          ? { ...item, selling_price: newPrice }
          : item
      )
    );
  };

  // Calculate totals
  const totalAmount = cartItems.reduce((sum, item) => sum + (item.selling_price || 0), 0);
  const advanceAmountNum = parseFloat(advanceAmount) || 0;
  const pendingAmount = totalAmount - advanceAmountNum;


  // Clear form function
  const clearForm = () => {
    setCartItems([]);
    setAdvanceAmount('');
    setNotes('');
    setSearchResults([]);
    setInventorySearchTerm('');
    setHasSearched(false);
    setSelectedDealer(null);
    setIsDealerDropdownOpen(false);
    setSelectedFormBranchId('');
    setSelectedFormBranchName('');
    
    // Reset scanner state
    setLastScannedValue('');
    setIsModalBranchDropdownOpen(false);
  };

  // Handle modal close
  const handleModalClose = () => {
    clearForm();
    setIsNewSaleModalOpen(false);
  };

  // Handle edit order click
  const handleEditOrderClick = (order: any) => {
    setEditOrderData({
      id: order.id,
      invoice_id: order.invoice_id,
      dealer_name: order.dealer_name,
      total_sale_price: order.total_sale_price,
      advance_amount: order.advance_amount,
      pending_amount: order.pending_amount,
      status: order.status,
      notes: order.notes || ''
    });
    setIsEditOrderModalOpen(true);
  };

  // Handle edit modal close
  const handleEditModalClose = () => {
    setEditOrderData(null);
    setIsEditOrderModalOpen(false);
  };

  // Handle edit order submission
  const handleEditOrderSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editOrderData || !token) return;

    try {
      const response = await fetch(`${getApiUrl(`dealers/sales/${editOrderData.id}`)}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          advance_amount: parseFloat(editOrderData.advance_amount),
          notes: editOrderData.notes
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          showToast('Dealer sale updated successfully!', 'success');
          handleEditModalClose();
          fetchDealerOrders(); // Refresh the list
        } else {
          showToast(result.error || 'Failed to update dealer sale', 'error');
        }
      } else {
        const errorData = await response.json();
        showToast(errorData.error || 'Failed to update dealer sale', 'error');
      }
    } catch (error) {
      console.error('Error updating dealer sale:', error);
      showToast('An error occurred while updating the dealer sale', 'error');
    }
  };

  // Handle dealer sale submission
  const handleDealerSaleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (cartItems.length === 0) {
      showToast('Please add at least one item to the cart', 'warning');
      return;
    }
    
    if (!selectedDealer) {
      showToast('Please select a dealer first', 'warning');
      return;
    }

    if (!selectedFormBranchId) {
      showToast('Please select a branch for this sale', 'warning');
      return;
    }
    
    if (totalAmount <= 0) {
      showToast('Total amount must be greater than 0', 'warning');
      return;
    }

    try {

      // Prepare dealer sale data
      const dealerSaleData = {
        dealer_id: selectedDealer.id,
        branch_id: selectedFormBranchId,
        cart_items: cartItems,
        total_amount: totalAmount,
        advance_amount: advanceAmountNum,
        pending_amount: pendingAmount,
        sale_date: new Date().toISOString().split('T')[0],
        notes: notes,
        payment_status: pendingAmount <= 0 ? 'fully_paid' : advanceAmountNum > 0 ? 'partial_paid' : 'pending'
      };

      const response = await fetch(getApiUrl("dealers/sales"), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dealerSaleData)
      });

      if (response.ok) {
        const result = await response.json();
        
        if (result.success) {
          showToast(`Dealer sale created successfully! Invoice ID: ${result.sale.invoice_id}`, 'success');
          
          // Reset form and close modal
          handleModalClose();
          
          // Refresh dealer orders list
          fetchDealerOrders();
        } else {
          showToast(result.error || 'Failed to create dealer sale. Please try again.', 'error');
        }
      } else if (response.status === 404) {
        // Endpoint not implemented yet
        console.log('Dealer sales endpoint not implemented yet');
        showToast('Dealer sales feature is under development. Endpoint not available yet.', 'info');
      } else {
        const errorText = await response.text();
        console.log('API error response:', errorText);
        showToast(`Failed to create dealer sale: ${response.status} ${response.statusText}`, 'error');
      }
      
    } catch (error) {
      showToast('Network error. Please check your connection and try again.', 'error');
    }
  };

  // Load dealer orders when component mounts or branch selection changes
  useEffect(() => {
    fetchDealerOrders();
  }, [fetchDealerOrders]);

  // Load inventory, dealers and branches when component mounts or modal opens  
  useEffect(() => {
    if (isNewSaleModalOpen) {
      fetchAvailableInventory();
      fetchAllDealers();
      fetchAvailableBranches();
    }
  }, [isNewSaleModalOpen, selectedBranchId, isAllBranches, user?.role]);
  
  // Also refresh inventory when branch selection changes for open modal
  useEffect(() => {
    if (isNewSaleModalOpen && token) {
      fetchAvailableInventory();
    }
  }, [selectedBranchId, isAllBranches]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isDealerDropdownOpen) {
        const target = event.target as HTMLElement;
        const dropdown = target.closest('.dealer-dropdown');
        if (!dropdown) {
          setIsDealerDropdownOpen(false);
        }
      }

      if (isModalBranchDropdownOpen) {
        const target = event.target as HTMLElement;
        const branchDropdown = target.closest('.branch-dropdown');
        if (!branchDropdown) {
          setIsModalBranchDropdownOpen(false);
        }
      }
      
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target as Node)) {
        setIsFilterDropdownOpen(false);
      }
      // Close time filter dropdown if clicking outside
      if (timeFilterRef.current && !timeFilterRef.current.contains(event.target as Node)) {
        setIsTimeFilterOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDealerDropdownOpen, isModalBranchDropdownOpen]);

  // Filter and sort options
  const filterOptions = ['All Orders', 'Partial Payment', 'Fully Paid'];

  // Filter and sort dealer orders
  const getFilteredAndSortedOrders = () => {
    let filtered = dealerOrders;

    // Apply search filter using activeSearchTerm
    if (activeSearchTerm.trim()) {
      filtered = filtered.filter(order => 
        order.invoice_id?.toLowerCase().includes(activeSearchTerm.toLowerCase()) ||
        order.dealer_name?.toLowerCase().includes(activeSearchTerm.toLowerCase()) ||
        order.notes?.toLowerCase().includes(activeSearchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (filterBy !== 'All Orders') {
      const statusMap: { [key: string]: string } = {
        'Partial Payment': 'partial_paid',
        'Fully Paid': 'fully_paid'
      };
      filtered = filtered.filter(order => order.status === statusMap[filterBy]);
    }

    // Sort by newest first (default)
    filtered.sort((a, b) => {
      return new Date(b.sale_date).getTime() - new Date(a.sale_date).getTime();
    });

    return filtered;
  };

  // Check permissions
  if (!checkPermission(user?.role, 'dealers')) {
    return <AccessDenied resource="Dealer Management" />;
  }

  // Sample data
  const dealerData: Dealer[] = [
    {
      id: 'DEALER001',
      dealerName: 'Rajesh Electronics',
      shopName: 'Mobile World',
      shopAddress: 'Shop No. 15, Electronics Market, Mumbai, MH 400001',
      phoneNumber: '+91 98765 43210',
      email: 'rajesh@mobileworld.com',
      gstNumber: '27ABCDE1234F1Z5',
      creditTerms: 30,
      totalBusiness: 850000,
      outstandingBalance: 125000,
      paymentStatus: 'Good',
      registrationDate: '2023-03-15',
      lastOrderDate: '2024-01-15',
      averageOrderValue: 45000,
      totalOrders: 19,
      notes: 'Bulk buyer, good payment history'
    },
    {
      id: 'DEALER002',
      dealerName: 'Amit Kumar',
      shopName: 'TechZone Solutions',
      shopAddress: 'A-123, Connaught Place, Delhi, DL 110001',
      phoneNumber: '+91 87654 32109',
      email: 'amit@techzone.com',
      creditTerms: 15,
      totalBusiness: 650000,
      outstandingBalance: 85000,
      paymentStatus: 'Overdue',
      registrationDate: '2023-06-20',
      lastOrderDate: '2024-01-10',
      averageOrderValue: 38500,
      totalOrders: 17,
      notes: 'Payment delays recently'
    },
    {
      id: 'DEALER003',
      dealerName: 'Priya Sharma',
      shopName: 'Digital Hub',
      shopAddress: 'Plot 45, IT Park, Bangalore, KA 560001',
      phoneNumber: '+91 76543 21098',
      gstNumber: '29ABCDE1234F1Z5',
      creditTerms: 45,
      totalBusiness: 420000,
      outstandingBalance: 65000,
      paymentStatus: 'Good',
      registrationDate: '2023-09-10',
      lastOrderDate: '2024-01-08',
      averageOrderValue: 35000,
      totalOrders: 12
    },
    {
      id: 'DEALER004',
      dealerName: 'Vikash Mobile',
      shopName: 'Smart Phones Plus',
      shopAddress: 'Shop 78, City Mall, Chennai, TN 600001',
      phoneNumber: '+91 65432 10987',
      creditTerms: 21,
      totalBusiness: 125000,
      outstandingBalance: 35000,
      paymentStatus: 'New',
      registrationDate: '2024-01-05',
      lastOrderDate: '2024-01-05',
      averageOrderValue: 41667,
      totalOrders: 3,
      notes: 'New dealer, first orders'
    },
    {
      id: 'DEALER005',
      dealerName: 'Sneha Communications',
      shopName: 'Mobile Palace',
      shopAddress: 'Sector 18, Noida, UP 201301',
      phoneNumber: '+91 54321 09876',
      email: 'sneha@mobilepalace.com',
      gstNumber: '09ABCDE1234F1Z5',
      creditTerms: 30,
      totalBusiness: 380000,
      outstandingBalance: 95000,
      paymentStatus: 'Overdue',
      registrationDate: '2023-07-25',
      lastOrderDate: '2023-12-20',
      averageOrderValue: 31667,
      totalOrders: 12,
      notes: 'Good volume, payment follow-up needed'
    }
  ];

  // Sample dealer orders data - individual transactions (one item per order)
  const dealerOrdersData: DealerOrder[] = [
    {
      id: 'ORDER001',
      orderId: 'ORD-2024-001',
      dealerId: 'DEALER001',
      dealerName: 'Rajesh Electronics',
      orderDate: '2024-01-15',
      totalAmount: 125000,
      advancePaid: 50000,
      balancePending: 75000,
      paymentStatus: 'Partial',
      itemsCount: 1,
      deliveryDate: '2024-01-20',
      deliveryStatus: 'Delivered',
      notes: 'iPhone 14 Pro Max 128GB'
    },
    {
      id: 'ORDER002',
      orderId: 'ORD-2024-002',
      dealerId: 'DEALER001',
      dealerName: 'Rajesh Electronics',
      orderDate: '2024-01-18',
      totalAmount: 95000,
      advancePaid: 95000,
      balancePending: 0,
      paymentStatus: 'Paid',
      itemsCount: 1,
      deliveryDate: '2024-01-22',
      deliveryStatus: 'Delivered',
      notes: 'Samsung Galaxy S23 Ultra 256GB'
    },
    {
      id: 'ORDER003',
      orderId: 'ORD-2024-003',
      dealerId: 'DEALER002',
      dealerName: 'Amit Kumar',
      orderDate: '2024-01-20',
      totalAmount: 180000,
      advancePaid: 80000,
      balancePending: 100000,
      paymentStatus: 'Pending',
      itemsCount: 1,
      deliveryDate: '2024-01-25',
      deliveryStatus: 'Pending',
      notes: 'iPhone 15 Pro Max 512GB'
    },
    {
      id: 'ORDER004',
      orderId: 'ORD-2024-004',
      dealerId: 'DEALER003',
      dealerName: 'Priya Sharma',
      orderDate: '2024-01-22',
      totalAmount: 65000,
      advancePaid: 30000,
      balancePending: 35000,
      paymentStatus: 'Partial',
      itemsCount: 1,
      deliveryDate: '2024-01-28',
      deliveryStatus: 'Pending',
      notes: 'OnePlus 11 5G 256GB'
    },
    {
      id: 'ORDER005',
      orderId: 'ORD-2024-005',
      dealerId: 'DEALER004',
      dealerName: 'Vikash Mobile',
      orderDate: '2024-01-23',
      totalAmount: 45000,
      advancePaid: 45000,
      balancePending: 0,
      paymentStatus: 'Paid',
      itemsCount: 1,
      deliveryDate: '2024-01-26',
      deliveryStatus: 'Delivered',
      notes: 'Google Pixel 8 128GB'
    }
  ];


  const getPaymentStatusBadge = (status: string) => {
    const statusConfig = {
      // Dealer sales statuses
      'pending': 'bg-red-100 text-red-800',
      'partial_paid': 'bg-orange-100 text-orange-800', 
      'fully_paid': 'bg-green-100 text-green-800',
      // Legacy statuses
      'Good': 'bg-green-100 text-green-800',
      'Overdue': 'bg-red-100 text-red-800',
      'New': 'bg-blue-100 text-blue-800'
    };
    return statusConfig[status as keyof typeof statusConfig] || 'bg-gray-100 text-gray-800';
  };

  // Icons
  const SearchIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  );

  const FilterIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z" />
    </svg>
  );

  const PlusIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  );

  const ExportIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
    </svg>
  );

  const UserIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );

  const ShoppingCartIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l-2.5-5M17 21a2 2 0 100-4 2 2 0 000 4zM9 21a2 2 0 100-4 2 2 0 000 4z" />
    </svg>
  );

  const CreditCardIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  );

  const getDeliveryStatusBadge = (status: string) => {
    const statusConfig = {
      'Pending': 'bg-yellow-100 text-yellow-800',
      'Delivered': 'bg-green-100 text-green-800',
      'Cancelled': 'bg-red-100 text-red-800'
    };
    return statusConfig[status as keyof typeof statusConfig] || 'bg-gray-100 text-gray-800';
  };

  return (
    <>
      {/* Toast Notification */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
      
      <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Dealer Orders</h1>
          <p className="text-gray-500 mt-1">
            Track and manage all dealer orders and transactions
            {!isAllBranches && selectedBranchId ? (
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                📍 Filtering: {selectedBranchName}
              </span>
            ) : (
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                🌐 Showing: All Branches
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mt-4 lg:mt-0">
          <button 
            onClick={exportToCSV}
            className="w-full sm:w-auto px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center justify-center gap-2 transition-colors h-10"
          >
            <ExportIcon />
            <span className="whitespace-nowrap">Export Orders</span>
          </button>
          <button 
            onClick={() => setIsNewSaleModalOpen(true)}
            className="w-full sm:w-auto px-4 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 flex items-center justify-center gap-2 transition-colors h-10"
          >
            <PlusIcon />
            <span className="whitespace-nowrap">New Dealer Order</span>
          </button>
        </div>
      </div>


      {/* Branch Filter Info */}
      {!isAllBranches && selectedBranchId && user?.role !== 'branch_manager' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Branch Filter Active
              </h3>
              <div className="mt-1 text-sm text-blue-700">
                Showing only dealer orders from <strong>{selectedBranchName}</strong>. 
                Select "All Branches" to see orders from all locations.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards - uses filtered data to match displayed table */}
      {(() => {
        const filteredOrders = getFilteredAndSortedOrders();
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Orders</p>
                  <p className="text-2xl font-bold text-gray-900">{filteredOrders.length}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Profit</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(filteredOrders.reduce((total, order) => total + (order.total_profit || 0), 0))}
                  </p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Outstanding Amount</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(filteredOrders.reduce((sum, order) => sum + (order.pending_amount || 0), 0))}</p>
                </div>
                <div className="p-3 bg-red-100 rounded-full">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Order Value</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(filteredOrders.reduce((sum, order) => sum + (order.total_sale_price || 0), 0))}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        );
      })()}


      {/* Search and Filters */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Search - Left side */}
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SearchIcon />
              </div>
              <input
                type="text"
                placeholder="Search by order ID, dealer, or notes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                className="block w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                style={{ width: '360px' }}
              />
              {activeSearchTerm && (
                <button
                  onClick={handleClearSearch}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  title="Clear search"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            <button
              onClick={handleSearch}
              className="px-4 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors h-10 whitespace-nowrap flex items-center justify-center"
            >
              Search
            </button>
          </div>

          {/* Filters - Right side */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Date Filter Dropdown */}
            <div className="relative" ref={timeFilterRef}>
              <button 
                onClick={() => setIsTimeFilterOpen(!isTimeFilterOpen)}
                className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
              >
                <span className="text-sm font-medium text-gray-700">
                  {selectedTimeFilter === 'Custom' && isCustomDateRange 
                    ? `${customStartDate} to ${customEndDate}` 
                    : selectedTimeFilter
                  }
                </span>
                <svg className={`w-4 h-4 text-gray-400 transition-transform ${isTimeFilterOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {isTimeFilterOpen && (
                <div className="absolute left-0 mt-2 w-36 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  {timeFilterOptions.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => handleTimeFilterSelect(option)}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                        selectedTimeFilter === option ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Filter By Dropdown */}
            <div className="relative" ref={filterDropdownRef}>
              <button 
                onClick={() => setIsFilterDropdownOpen(!isFilterDropdownOpen)}
                className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
              >
                <FilterIcon />
                <span className="text-sm font-medium text-gray-700">
                  {filterBy}
                </span>
                <svg className={`w-4 h-4 text-gray-400 transition-transform ${isFilterDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {isFilterDropdownOpen && (
                <div className="absolute left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  {filterOptions.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setFilterBy(option);
                        setIsFilterDropdownOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                        filterBy === option ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button 
              onClick={() => {
                setSearchTerm('');
                setActiveSearchTerm('');
                setFilterBy('All Orders');
                setSelectedTimeFilter('All Time');
                setIsCustomDateRange(false);
                setIsFilterDropdownOpen(false);
                setIsTimeFilterOpen(false);
              }}
              className="flex items-center gap-2 px-4 py-2.5 text-gray-600 hover:text-gray-900 transition-colors border border-gray-200 rounded-lg hover:border-gray-300 bg-white h-10"
            >
              <FilterIcon />
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Dealer Orders</h3>
          <p className="text-sm text-gray-500 mt-1">Individual transactions and orders from dealers</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dealer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Advance Paid</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoadingOrders ? (
                <tr>
                  <td colSpan={9} className="px-6 py-8 text-center">
                    <LoadingSpinner />
                    <p className="text-gray-500 mt-2">Loading dealer orders...</p>
                  </td>
                </tr>
              ) : getFilteredAndSortedOrders().length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-8 text-center text-gray-500">
                    {activeSearchTerm || filterBy !== 'All Orders' ? 'No orders match your search criteria' : 'No dealer orders found'}
                  </td>
                </tr>
              ) : (
                getFilteredAndSortedOrders().map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {new Date(order.sale_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-primary-600">{order.invoice_id}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{order.dealer_name}</div>
                      <div className="text-xs text-gray-500">{order.dealer_shop}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {order.item_details?.phone_model || `${order.items_count || 0} items`}
                      </div>
                      <div className="text-sm text-gray-500 font-mono">
                        IMEI/MAC: {order.item_details?.imei_mac_address || 'N/A'}
                        {order.item_details?.phone_model === 'Multiple Items' && (
                          <div className="text-xs text-blue-600 mt-1">Click the view icon →</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCurrency(order.total_sale_price)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                      {formatCurrency(order.advance_amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <span className={`${(order.pending_amount || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(order.pending_amount)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getPaymentStatusBadge(order.status)}`}>
                        {order.status === 'pending' ? 'Pending' : order.status === 'partial_paid' ? 'Partial' : 'Fully Paid'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => {
                            setSelectedOrder(order);
                            setIsViewOrderModalOpen(true);
                          }}
                          className="text-primary-600 hover:text-primary-900" 
                          title="View Details"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                        <button 
                          onClick={() => handleEditOrderClick(order)}
                          className="text-blue-600 hover:text-blue-900" 
                          title="Edit Payment"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* New Dealer Sale Modal */}
      {isNewSaleModalOpen && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          onClick={handleModalClose}
        >
          <div 
            className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto relative m-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {selectedDealer ? `New Order - ${selectedDealer.name}` : 'New Dealer Order'}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Creating a new individual order transaction
                </p>
              </div>
              <button
                onClick={handleModalClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form className="space-y-6" onSubmit={handleDealerSaleSubmit}>
              {/* Order Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Order Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Order Date *
                    </label>
                    <input
                      type="date"
                      defaultValue={new Date().toISOString().split('T')[0]}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Expected Delivery
                    </label>
                    <input
                      type="date"
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                    />
                  </div>
                </div>
              </div>

              {/* Dealer Selection */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Dealer Information</h3>
                
                {/* Dealer Dropdown */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Dealer *
                  </label>
                  <div className="flex gap-2">
                    <div className="relative flex-1 dealer-dropdown">
                      <button 
                        type="button"
                        onClick={() => setIsDealerDropdownOpen(!isDealerDropdownOpen)}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10 flex items-center justify-between text-left"
                        disabled={isLoadingDealers}
                      >
                        <span className={selectedDealer ? 'text-gray-900' : 'text-gray-500'}>
                          {isLoadingDealers 
                            ? 'Loading dealers...' 
                            : selectedDealer 
                              ? `${selectedDealer.name} - ${selectedDealer.shop_name}` 
                              : 'Select a dealer...'}
                        </span>
                        <svg className={`w-4 h-4 text-gray-400 transition-transform ${isDealerDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      
                      {/* Dropdown menu */}
                      {isDealerDropdownOpen && !isLoadingDealers && (
                        <div className="absolute left-0 mt-2 w-full bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 max-h-60 overflow-y-auto">
                          {allDealers.length > 0 ? (
                            allDealers.map((dealer) => (
                              <button
                                key={dealer.id}
                                type="button"
                                onClick={() => handleDealerSelect(dealer)}
                                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors"
                              >
                                <div>
                                  <div className="font-medium text-gray-900">{dealer.name}</div>
                                  <div className="text-xs text-gray-500">{dealer.shop_name} • {dealer.phone}</div>
                                </div>
                              </button>
                            ))
                          ) : (
                            <div className="px-4 py-2 text-sm text-gray-500">No dealers found</div>
                          )}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={handleAddNewDealer}
                      className="px-4 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors h-10 flex items-center gap-2 whitespace-nowrap"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add New
                    </button>
                  </div>
                </div>


                {/* Branch Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Branch *
                  </label>
                  <div className="relative branch-dropdown">
                    <button 
                      type="button"
                      onClick={() => setIsModalBranchDropdownOpen(!isModalBranchDropdownOpen)}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10 flex items-center justify-between text-left"
                      disabled={user?.role === 'branch_manager'}
                    >
                      <span className={selectedFormBranchName ? 'text-gray-900' : 'text-gray-500'}>
                        {selectedFormBranchName || 'Select a branch...'}
                      </span>
                      {user?.role !== 'branch_manager' && (
                        <svg className={`w-4 h-4 text-gray-400 transition-transform ${isModalBranchDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </button>
                    
                    {/* Branch Dropdown Menu */}
                    {isModalBranchDropdownOpen && user?.role !== 'branch_manager' && (
                      <div className="absolute left-0 mt-2 w-full bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 max-h-60 overflow-y-auto">
                        {availableBranches.length > 0 ? (
                          availableBranches.map((branch) => (
                            <button
                              key={branch.id}
                              type="button"
                              onClick={() => handleFormBranchSelect(branch.id, branch.branch_name)}
                              className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                                selectedFormBranchId === branch.id ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'
                              }`}
                            >
                              {branch.branch_name}
                            </button>
                          ))
                        ) : (
                          <div className="px-4 py-2 text-sm text-gray-500">
                            No branches available
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {user?.role === 'branch_manager' && (
                    <p className="text-xs text-gray-500 mt-1">
                      Branch managers can only create sales for their assigned branch
                    </p>
                  )}
                </div>
              </div>

              {/* Inventory Search */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Select Inventory Item</h3>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Search Inventory Item *
                    </label>
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V6a1 1 0 00-1-1H5a1 1 0 00-1 1v1a1 1 0 001 1zm12 0h2a1 1 0 001-1V6a1 1 0 00-1-1h-2a1 1 0 00-1 1v1a1 1 0 001 1zM5 20h2a1 1 0 001-1v-1a1 1 0 00-1-1H5a1 1 0 00-1 1v1a1 1 0 001 1z" />
                      </svg>
                      <span className="text-sm text-green-600 font-medium">Scanner Ready</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                      <input
                        type="text"
                        value={inventorySearchTerm}
                        onChange={(e) => setInventorySearchTerm(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleInventorySearch())}
                        className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                        placeholder="Search by IMEI, Inventory ID, Model, or scan barcode..."
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleInventorySearch}
                      className="px-4 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors h-10 flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      Search
                    </button>
                  </div>

                  {/* Barcode Scanner - Always Active */}
                  <BarcodeScanner
                    onScan={handleBarcodeScan}
                    isActive={isNewSaleModalOpen}
                    className="hidden"
                  />
                  
                  {/* Search Results */}
                  {isLoadingInventory && (
                    <div className="mt-3 p-4 text-center">
                      <LoadingSpinner size="sm" text="Loading inventory..." />
                    </div>
                  )}
                  
                  {!isLoadingInventory && searchResults.length > 0 && (
                    <div className="mt-3 border border-gray-200 rounded-lg max-h-60 overflow-y-auto">
                      <div className="space-y-2 p-2">
                        {searchResults.map((item) => (
                          <div key={item.id} className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 mb-1">{item.phone_model}</p>
                              <p className="text-xs text-gray-500 mb-1">IMEI: {item.imei_mac_address}</p>
                              <p className="text-xs text-gray-400">ID: {item.inventory_id}</p>
                              <p className="text-xs text-gray-400 mt-1">Minimum Sellable Cost: {formatCurrency(item.selling_price)}</p>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <div className="relative">
                                <span className="absolute left-3 top-2.5 text-sm text-gray-500">{getCurrencySymbol()}</span>
                                <input
                                  type="text"
                                  placeholder="Price"
                                  className="w-36 pl-8 pr-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                />
                              </div>
                              <button
                                type="button"
                                onClick={(e) => {
                                  const input = e.currentTarget.previousElementSibling?.querySelector('input') as HTMLInputElement;
                                  const price = parseFloat(input.value);
                                  if (price > 0) {
                                    addToCart(item, price);
                                    input.value = '';
                                  }
                                }}
                                className="px-4 py-2.5 bg-primary-500 text-white text-sm rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
                              >
                                Add to Cart
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {!isLoadingInventory && searchResults.length === 0 && hasSearched && inventorySearchTerm && (
                    <div className="mt-3 p-3 text-sm text-gray-500 text-center border border-gray-200 rounded-lg">
                      No available inventory items found. Try searching with different keywords.
                    </div>
                  )}
                </div>
              </div>

              {/* Cart Items */}
              {cartItems.length > 0 && (
                <div className="border-t pt-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Selected Items ({cartItems.length})</h3>
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {cartItems.map((cartItem) => (
                      <div key={cartItem.inventory_item.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        {/* Item Details */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 mb-1">{cartItem.inventory_item.phone_model}</p>
                          <p className="text-xs text-gray-500 mb-1">IMEI: {cartItem.inventory_item.imei_mac_address}</p>
                          <p className="text-xs text-gray-400">ID: {cartItem.inventory_item.inventory_id}</p>
                        </div>
                        
                        {/* Selling Price Input */}
                        <div className="flex-shrink-0">
                          <label className="block text-xs text-gray-500 mb-1">Dealer Price</label>
                          <div className="relative">
                            <span className="absolute left-3 top-2.5 text-sm text-gray-500">{getCurrencySymbol()}</span>
                            <input
                              type="number"
                              value={cartItem.selling_price}
                              onChange={(e) => updateCartItemPrice(cartItem.inventory_item.id, parseFloat(e.target.value) || 0)}
                              className="w-36 pl-8 pr-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="0"
                              min="0"
                            />
                          </div>
                        </div>
                        
                        {/* Delete Button */}
                        <div className="flex-shrink-0 flex items-center justify-center">
                          <button
                            type="button"
                            onClick={() => removeFromCart(cartItem.inventory_item.id)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                            title="Remove item"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Payment Summary */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Payment Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Total Order Amount
                    </label>
                    <input
                      type="text"
                      value={formatCurrency(totalAmount)}
                      readOnly
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 font-medium h-10"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Advance Amount *
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-2.5 text-gray-500">{getCurrencySymbol()}</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={advanceAmount}
                        onChange={(e) => setAdvanceAmount(e.target.value)}
                        placeholder="Enter advance amount"
                        className="w-full pl-8 pr-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pending Amount
                    </label>
                    <input
                      type="text"
                      value={formatCurrency(pendingAmount)}
                      readOnly
                      className={`w-full px-3 py-2.5 border border-gray-200 rounded-lg bg-gray-50 font-medium h-10 ${
                        pendingAmount > 0 ? 'text-red-600' : 'text-green-600'
                      }`}
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Status
                  </label>
                  <div className="flex items-center h-10">
                    <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${
                      pendingAmount <= 0 
                        ? 'bg-green-100 text-green-800' 
                        : advanceAmountNum > 0 
                          ? 'bg-yellow-100 text-yellow-800' 
                          : 'bg-red-100 text-red-800'
                    }`}>
                      {pendingAmount <= 0 ? 'Fully Paid' : advanceAmountNum > 0 ? 'Partial Payment' : 'Pending'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Order Summary */}
              {cartItems.length > 0 && (
                <div className="border-t pt-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Order Summary</h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Total Items:</span>
                      <span className="text-sm font-medium text-gray-900">{cartItems.length}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Total Our Cost:</span>
                      <span className="text-sm font-medium text-gray-900">{formatCurrency(cartItems.reduce((sum, item) => sum + ((typeof item.inventory_item.buying_price === 'string' ? parseFloat(item.inventory_item.buying_price) : item.inventory_item.buying_price) + (typeof item.inventory_item.service_expense === 'string' ? parseFloat(item.inventory_item.service_expense) : item.inventory_item.service_expense) + (typeof item.inventory_item.package_expense === 'string' ? parseFloat(item.inventory_item.package_expense) : item.inventory_item.package_expense)), 0))}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Total Dealer Price:</span>
                      <span className="text-sm font-medium text-gray-900">{formatCurrency(totalAmount)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Advance Received:</span>
                      <span className="text-sm font-medium text-gray-900">{formatCurrency(advanceAmountNum)}</span>
                    </div>
                    <div className="border-t pt-2">
                      <div className="flex justify-between items-center">
                        <span className="text-base font-medium text-gray-900">Expected Profit:</span>
                        <span className="text-base font-bold text-green-600">{formatCurrency(totalAmount - cartItems.reduce((sum, item) => sum + ((typeof item.inventory_item.buying_price === 'string' ? parseFloat(item.inventory_item.buying_price) : item.inventory_item.buying_price) + (typeof item.inventory_item.service_expense === 'string' ? parseFloat(item.inventory_item.service_expense) : item.inventory_item.service_expense) + (typeof item.inventory_item.package_expense === 'string' ? parseFloat(item.inventory_item.package_expense) : item.inventory_item.package_expense)), 0))}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Notes */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Information</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Order Notes (Optional)
                  </label>
                  <textarea
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors resize-none"
                    placeholder="Add any special terms, delivery instructions, or other notes for this order..."
                  ></textarea>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-3 pt-6 border-t">
                <button
                  type="button"
                  onClick={handleModalClose}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={cartItems.length === 0}
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Create Order
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* View Order Details Modal */}
      {isViewOrderModalOpen && selectedOrder && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          onClick={() => setIsViewOrderModalOpen(false)}
        >
          <div 
            className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto relative m-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Order Details - {selectedOrder.invoice_id}</h2>
              <button
                onClick={() => setIsViewOrderModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="space-y-6">
              {/* Dealer Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Dealer Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Dealer Name</p>
                    <p className="text-sm text-gray-900">{selectedOrder.dealer_name}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Shop Name</p>
                    <p className="text-sm text-gray-900">{selectedOrder.dealer_shop}</p>
                  </div>
                </div>
              </div>

              {/* Order Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Order Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Invoice ID</p>
                    <p className="text-sm font-mono text-gray-900">{selectedOrder.invoice_id}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Order Date</p>
                    <p className="text-sm text-gray-900">{new Date(selectedOrder.sale_date).toLocaleDateString('en-IN')}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Created Date</p>
                    <p className="text-sm text-gray-900">{new Date(selectedOrder.created_at).toLocaleDateString('en-IN')}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Payment Status</p>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      selectedOrder.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      selectedOrder.status === 'partial_paid' ? 'bg-blue-100 text-blue-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {selectedOrder.status === 'pending' ? 'Pending' : 
                       selectedOrder.status === 'partial_paid' ? 'Partial Payment' : 'Fully Paid'}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Items Count</p>
                    <p className="text-sm text-gray-900">{selectedOrder.items_count} items</p>
                  </div>
                </div>
                {selectedOrder.notes && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-600">Order Notes</p>
                    <p className="text-sm text-gray-900">{selectedOrder.notes}</p>
                  </div>
                )}
              </div>

              {/* Product Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Product Information</h3>
                {selectedOrder.item_details?.type === 'single' ? (
                  // Single item - show as grid
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Device Model</p>
                      <p className="text-sm text-gray-900">{selectedOrder.item_details.phone_model}</p>
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-sm font-medium text-gray-600">IMEI/MAC Address</p>
                      <p className="text-sm font-mono text-gray-900">{selectedOrder.item_details.imei_mac_address}</p>
                    </div>
                  </div>
                ) : selectedOrder.detailed_items && selectedOrder.detailed_items.length > 0 ? (
                  // Multiple items - show as table
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600">This order contains {selectedOrder.items_count} items:</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-2 text-gray-600 font-medium">Device Model</th>
                            <th className="text-left py-2 text-gray-600 font-medium">Category</th>
                            <th className="text-left py-2 text-gray-600 font-medium">IMEI/MAC</th>
                            <th className="text-left py-2 text-gray-600 font-medium">Selling Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedOrder.detailed_items.map((item: any, index: number) => (
                            <tr key={item.id} className="border-b border-gray-100">
                              <td className="py-2 text-gray-900">{item.phone_model}</td>
                              <td className="py-2">
                                <span className="text-gray-900">
                                  {item.category.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                                </span>
                              </td>
                              <td className="py-2 font-mono text-gray-900">{item.imei_mac_address}</td>
                              <td className="py-2 text-gray-900 font-medium">{formatCurrency(item.selling_price)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  // Fallback for no detailed items
                  <div className="text-center py-8">
                    <div className="text-gray-500 mb-2">
                      <svg className="w-12 h-12 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                    </div>
                    <p className="text-sm font-medium text-gray-900">Multiple Items Order</p>
                    <p className="text-sm text-gray-500">This order contains {selectedOrder.items_count} items</p>
                    <p className="text-xs text-blue-600 mt-2">Item details not available</p>
                  </div>
                )}
              </div>

              {/* Financial Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Payment Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Amount</p>
                    <p className="text-lg font-semibold text-gray-900">{formatCurrency(selectedOrder.total_sale_price)}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Advance Paid</p>
                    <p className="text-lg font-semibold text-green-600">{formatCurrency(selectedOrder.advance_amount)}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Balance Pending</p>
                    <p className="text-lg font-semibold text-red-600">{formatCurrency(selectedOrder.pending_amount)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Edit Order Modal */}
      {isEditOrderModalOpen && editOrderData && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          onClick={handleEditModalClose}
        >
          <div 
            className="bg-white rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto relative m-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Edit Payment</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Update payment information for order {editOrderData.invoice_id}
                </p>
              </div>
              <button
                onClick={handleEditModalClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleEditOrderSubmit} className="space-y-4">
              {/* Order Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-2">Order Details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Dealer:</span>
                    <span className="font-medium">{editOrderData.dealer_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Amount:</span>
                    <span className="font-medium">{formatCurrency(editOrderData.total_sale_price)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Current Status:</span>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      editOrderData.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      editOrderData.status === 'partial_paid' ? 'bg-blue-100 text-blue-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {editOrderData.status === 'pending' ? 'Pending' : 
                       editOrderData.status === 'partial_paid' ? 'Partial' : 'Paid'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Payment Fields */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Advance Amount Received *
                  </label>
                  <input
                    type="number"
                    value={editOrderData.advance_amount}
                    onChange={(e) => setEditOrderData({
                      ...editOrderData,
                      advance_amount: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                    placeholder="Enter received amount"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Pending: {formatCurrency(editOrderData.total_sale_price - parseFloat(editOrderData.advance_amount || 0))}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    value={editOrderData.notes}
                    onChange={(e) => setEditOrderData({
                      ...editOrderData,
                      notes: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    rows={3}
                    placeholder="Add payment notes..."
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-6 border-t">
                <button
                  type="button"
                  onClick={handleEditModalClose}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Update Payment
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Custom Date Range Modal */}
      {isCustomDateModalOpen && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-xl p-6 w-full max-w-md m-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Select Date Range</h2>
              <button
                onClick={handleCustomDateCancel}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  From Date *
                </label>
                <input
                  type="date"
                  value={tempStartDate}
                  onChange={(e) => setTempStartDate(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  To Date *
                </label>
                <input
                  type="date"
                  value={tempEndDate}
                  onChange={(e) => setTempEndDate(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
              
              {/* Date range info */}
              {tempStartDate && tempEndDate && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">
                    Selected range: <span className="font-medium">{tempStartDate}</span> to <span className="font-medium">{tempEndDate}</span>
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {(() => {
                      const start = new Date(tempStartDate);
                      const end = new Date(tempEndDate);
                      const diffTime = Math.abs(end.getTime() - start.getTime());
                      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
                      return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
                    })()}
                  </p>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={handleCustomDateCancel}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCustomDateApply}
                disabled={!tempStartDate || !tempEndDate}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Apply
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      </div>
    </>
  );
};

export default DealerManagement;